/*
 * Daro segments: limits the EasySearch YMM dropdowns to the current segment's vehicles,
 * flags a garage vehicle that belongs to another segment, and scopes search forms.
 * Also records the chosen vehicle (window.DaroVehicle, snippets/daro-vehicle-redirect.liquid) the moment
 * Year, Make, Model and Submodel are all picked, so brands, packages and search follow it without a Search click.
 * Config comes from snippets/segment-data.liquid (#DaroSegments).
 *
 * Rules: "Ferrari" (whole make), "Audi > R8" (exact model), "Mercedes-Benz > AMG GT*" (model prefix).
 * A model rule beats a whole-make rule in another segment. Makes with no rule are hidden everywhere.
 */
(() => {
  const configEl = document.getElementById('DaroSegments');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  const norm = (s) => String(s || '').replace(/["']/g, '').trim().toLowerCase();
  // Only the server-resolved segment limits the YMM and search. The colour fallback restored from
  // localStorage (cart, plain search, /collections/all) must not restrict a neutral page.
  const current = config.current || '';

  // Parse rules into { handle, name, url, makes: Set, models: [{ make, model, prefix }] }
  const segments = (config.segments || []).map((seg) => {
    const makes = new Set();
    const models = [];
    (seg.rules || []).forEach((rule) => {
      if (!rule) return;
      const [make, model] = rule.split('>');
      if (model === undefined) {
        makes.add(norm(make));
      } else {
        const raw = model.trim();
        const prefix = raw.endsWith('*');
        models.push({ make: norm(make), model: norm(prefix ? raw.slice(0, -1) : raw), prefix });
      }
    });
    return { ...seg, makes, models };
  });
  if (!segments.length) return;

  const modelRuleMatches = (rule, make, model) =>
    rule.make === make && (rule.prefix ? model.startsWith(rule.model) : model === rule.model);

  // Which segment does this vehicle belong to? Model rules first, then whole makes.
  function segmentFor(make, model) {
    make = norm(make);
    model = norm(model);
    if (model) {
      const byModel = segments.find((s) => s.models.some((r) => modelRuleMatches(r, make, model)));
      if (byModel) return byModel;
    }
    return segments.find((s) => s.makes.has(make)) || null;
  }

  const activeSegments = () => {
    const seg = segments.find((s) => s.handle === current);
    return seg ? [seg] : segments; // outside a segment: union of all (hides unassigned makes)
  };

  function makeAllowed(make) {
    make = norm(make);
    return activeSegments().some((s) => s.makes.has(make) || s.models.some((r) => r.make === make));
  }

  function modelAllowed(make, model) {
    const owner = segmentFor(make, model);
    if (!owner) {
      // Make appears only via model rules and this model matches none of them
      return false;
    }
    return activeSegments().includes(owner);
  }

  const placeholderOf = (select) => norm((select.querySelector('option[value=""]') || {}).textContent);
  const isRealOption = (opt) => opt.value !== '' && !opt.disabled;
  const optionLabel = (opt) => opt.textContent || opt.value;

  function findSelects(container) {
    const selects = Array.from(container.querySelectorAll('.easysearch-select-holder select, select[id^="easysearch_field_"]'));
    return {
      make: selects.find((s) => placeholderOf(s) === 'select make'),
      model: selects.find((s) => placeholderOf(s) === 'select model'),
      year: selects.find((s) => placeholderOf(s) === 'select year'),
      submodel: selects.find((s) => placeholderOf(s) === 'select submodel'),
    };
  }

  function prune(select, allowed) {
    if (!select) return;
    Array.from(select.options).forEach((opt) => {
      if (isRealOption(opt) && !allowed(optionLabel(opt))) opt.remove();
    });
  }

  function selectedText(select) {
    if (!select || !select.value) return '';
    const opt = select.options[select.selectedIndex];
    return opt ? optionLabel(opt) : select.value;
  }

  // Garage / preselected vehicle that belongs to another segment
  function showNotice(container, vehicleText, owner) {
    let notice = container.parentElement && container.parentElement.querySelector(':scope > .daro-seg-notice');
    if (!owner || !current || owner.handle === current) {
      if (notice) notice.remove();
      return;
    }
    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'daro-seg-notice';
      notice.setAttribute('role', 'status');
      container.insertAdjacentElement('afterend', notice);
    }
    notice.textContent = `Your ${vehicleText} is in our ${owner.name} section. `;
    const link = document.createElement('a');
    link.href = owner.url;
    link.textContent = `Go to ${owner.name} →`;
    notice.appendChild(link);
  }

  function garageVehicleText(container) {
    const el = container.querySelector('.easysearch-garage-collapsed-title, .easysearch-garage-item.active, .easysearch-garage-selected');
    return el ? el.textContent.trim() : '';
  }

  // Find make + model inside free text like "2019 Ferrari 488 Pista"
  function ownerFromText(text) {
    const t = norm(text);
    for (const seg of segments) {
      for (const r of seg.models) {
        if (t.includes(`${r.make} ${r.model}`)) return seg;
      }
    }
    for (const seg of segments) {
      for (const make of seg.makes) {
        if (new RegExp(`(^|\\s)${make.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`).test(t)) return seg;
      }
    }
    return null;
  }

  // ---- Year / Make cross-check against EasySearch's own vehicle database ----
  // EasySearch builds Year -> Make -> Model from its CSV. Make/Model pruning alone leaves years with
  // no vehicle in this segment (e.g. 1986 = only a Toyota Supra), so read the same CSV (already in
  // the browser cache from EasySearch) and keep only years / makes that have an allowed vehicle.
  let yearIndex = null; // Map(year -> Set(normalised makes with an allowed vehicle that year))
  let vehicleIndex = null; // Map("year|make|model|submodel", normalised) -> Daro vehicle key (the vehicle page handle)

  // The vehicle key from a row's tag or collection cell, as scripts/fitment/lib.mjs vehicleSlug: the EasySearch
  // tag without "-esi<n>". A multi-year row keeps its first year ("2019-aston-martin-db11-amr" for a 2020).
  function rowKey(cells) {
    const cell = cells.find((c) => c.startsWith('>>') || c.startsWith('/collections/'));
    if (!cell) return null;
    const handle = cell.split('$$$')[0].replace(/^>>|^\/collections\//, '');
    if (!/-esi\d+$/.test(handle)) return null;
    return handle.replace(/-esi\d+$/, '').toLowerCase().replace(/-+/g, '-').replace(/^-|-$/g, '') || null;
  }

  function easySearchDbUrl() {
    for (const s of document.querySelectorAll('script.easysearch-initialization')) {
      const m = s.textContent.match(/"db":"([^"]+\.csv[^"]*)"/);
      if (m) {
        const url = m[1].replace(/\\\//g, '/');
        return url.startsWith('//') ? `https:${url}` : url;
      }
    }
    return null;
  }

  function parseCsvLine(line) {
    const cells = [];
    let cur = '';
    let quoted = false;
    for (const ch of line) {
      if (ch === '"') quoted = !quoted;
      else if (ch === ',' && !quoted) {
        cells.push(cur);
        cur = '';
      } else cur += ch;
    }
    cells.push(cur);
    return cells;
  }

  let indexRequested = false;
  function loadYearIndex() {
    if (indexRequested) return;
    indexRequested = true;
    const url = easySearchDbUrl();
    if (!url) return;
    fetch(url)
      .then((r) => (r.ok ? r.text() : ''))
      .then((text) => {
        const index = new Map();
        const vehicles = new Map();
        for (const line of text.split(/\r?\n/)) {
          if (!line) continue;
          const cells = parseCsvLine(line);
          const [year, make, model, submodel] = cells;
          const key = year && make ? rowKey(cells) : null;
          if (key) vehicles.set([year, make, model, submodel].map(norm).join('|'), key);
          if (!year || !make || !modelAllowed(make, model)) continue;
          // Some rows carry a range ("2021-2022"); count the vehicle under every year in it
          const range = year.trim().match(/^(\d{4})\s*-\s*(\d{4})$/);
          const years = [];
          if (range) for (let y = +range[1]; y <= +range[2]; y++) years.push(String(y));
          years.push(norm(year));
          for (const y of years) {
            if (!index.has(y)) index.set(y, new Set());
            index.get(y).add(norm(make));
          }
        }
        if (vehicles.size) vehicleIndex = vehicles;
        if (!index.size) {
          document.querySelectorAll('.easysearch-container').forEach((c) => captureVehicle(c, false));
          return;
        }
        yearIndex = index;
        document.querySelectorAll('.easysearch-container').forEach(apply);
      })
      .catch(() => {});
  }

  const vehicleName = (container) => {
    const { year, make, model, submodel } = findSelects(container);
    return [year, make, model, submodel].map(selectedText).filter(Boolean).join(' ');
  };

  // The chosen vehicle follows the finder. Theme setting "Apply the vehicle" (DaroVehicle.mode):
  // - 'search': only a Search click applies it (onFinderSearch below). The vehicle EasySearch fills back in
  //   on a later page (saved by that Search) is recorded too, until the shopper starts changing the selects.
  // - 'pick': all four picked -> recorded and saved for the next page; a shopper's own change that leaves it
  //   incomplete (or picks a combination with no vehicle page) -> forgotten.
  // EasySearch filling the selects in by itself only ever records, so a page whose finder starts empty
  // doesn't forget the shopper's vehicle.
  const touched = new WeakSet();
  function captureVehicle(container, byShopper) {
    const vehicle = window.DaroVehicle;
    if (!vehicle) return;
    if (vehicle.mode === 'search') {
      if (byShopper) touched.add(container);
      if (touched.has(container)) return;
    }
    const { year, make, model, submodel } = findSelects(container);
    const parts = [year, make, model, submodel].map(selectedText);
    if (vehicle.mode === 'search') {
      if (!parts.every(Boolean) || !vehicleIndex) return;
      const key = vehicleIndex.get(parts.map(norm).join('|'));
      if (key && key !== vehicle.key) vehicle.set(key, parts.join(' '));
      return;
    }
    if (parts.every(Boolean)) {
      if (!vehicleIndex) return; // recorded once EasySearch's database is read (loadYearIndex)
      const key = vehicleIndex.get(parts.map(norm).join('|'));
      if (key) {
        if (key !== vehicle.key) vehicle.set(key, parts.join(' '));
        rememberInFinder(container);
        return;
      }
    }
    if (byShopper && vehicle.key) vehicle.forget();
  }

  // EasySearch only remembers its selection when Search is clicked. Save it the same way it does
  // (its "easysearch-preselect": the selects' values, as in its getCurrentIndex), so every finder on the next
  // page shows the vehicle the pages are narrowed to. DaroVehicle.forget() removes it again.
  function rememberInFinder(container) {
    if (window.DaroVehicle && window.DaroVehicle.markSegment) window.DaroVehicle.markSegment();
    const values = Array.from(container.querySelectorAll('.easysearch-field-select-holder select')).map((s) => s.value);
    if (!values.length || values.some((v) => !v)) return;
    const value = values.map((v) => `${v}easysearch-preselect-delimiter`).join('');
    try {
      const saved = JSON.parse(localStorage.getItem('easysearch-preselect') || 'null');
      if (saved && saved.value === value) return;
      ['easysearch-preselect', 'easysearch-preselect-fitment'].forEach((k) => localStorage.setItem(k, JSON.stringify({ value, expires: null })));
    } catch (e) {}
  }

  function apply(container, byShopper) {
    const { make, model, year } = findSelects(container);
    loadYearIndex();
    captureVehicle(container, byShopper);

    // Read the chosen vehicle (restored by EasySearch or shown in the garage) before pruning,
    // so a vehicle from another segment can still be named in the notice.
    let owner = null;
    let text = '';
    if (make && make.value) {
      text = [selectedText(year), selectedText(make), selectedText(model)].filter(Boolean).join(' ');
      owner = segmentFor(selectedText(make), selectedText(model));
    } else {
      text = garageVehicleText(container);
      owner = text ? ownerFromText(text) : null;
    }

    const makeText = selectedText(make);
    const yearText = selectedText(year);
    if (yearIndex) {
      prune(year, (y) => yearIndex.has(norm(y)));
    }
    const yearMakes = yearIndex && yearText ? yearIndex.get(norm(yearText)) : null;
    prune(make, (m) => makeAllowed(m) && (!yearMakes || yearMakes.has(norm(m))));
    if (model && makeText) {
      prune(model, (m) => modelAllowed(makeText, m));
    }

    showNotice(container, text, owner);
  }

  const watched = new WeakSet();
  function watch(container) {
    patchSearchSubmit();
    if (watched.has(container)) return;
    watched.add(container);
    let queued = false;
    let byShopper = false;
    const run = () => {
      queued = false;
      apply(container, byShopper);
      byShopper = false;
    };
    const schedule = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(run);
      }
    };
    new MutationObserver(schedule).observe(container, { childList: true, subtree: true });
    container.addEventListener('change', (event) => {
      if (event.isTrusted) byShopper = true;
      schedule();
    });
    // EasySearch's Clear button empties the finder: forget the vehicle with it
    container.addEventListener('click', (event) => {
      if (event.isTrusted && event.target.closest('.easysearch-btn-clean, .easysearch-clear-holder button, .easysearch-clear-holder a')) {
        if (window.DaroVehicle) window.DaroVehicle.clear();
      }
    });
    schedule();
  }

  function scan() {
    document.querySelectorAll('.easysearch-container').forEach(watch);
  }

  // ---- YMM search goes straight to the vehicle page ----
  // EasySearch links to /collections/all/<vehicle>-esi<n> (or a segment collection), which
  // snippets/daro-vehicle-redirect.liquid then redirects to the vehicle page: a whole extra page
  // load, slow on mobile. Point the search at the vehicle page directly, with the same rules.
  const finderBases = new Set(config.finder || ['all']);

  function directVehicleUrl(href) {
    let url;
    try {
      url = new URL(href, location.href);
    } catch (e) {
      return null;
    }
    if (url.origin !== location.origin) return null;
    const m = url.pathname.match(/^(.*?)\/collections\/([^/]+)\/([^/]+)\/?$/);
    if (!m || !finderBases.has(decodeURIComponent(m[2]))) return null;
    let handle = null;
    for (const tag of decodeURIComponent(m[3]).split('+')) {
      const t = tag.match(/^(.+)-esi\d+$/);
      if (t) {
        // Shopify's handle form, as the vehicle pages were created: "road---track" -> "road-track", no trailing "-"
        handle = t[1].toLowerCase().replace(/-+/g, '-').replace(/^-|-$/g, '');
        break;
      }
    }
    if (!handle) return null;
    url.searchParams.delete('filter.p.tag');
    url.searchParams.delete('page');
    const q = url.searchParams.toString();
    return `${m[1]}/collections/${encodeURIComponent(handle)}${q ? `?${q}` : ''}${url.hash}`;
  }

  // A Search in the finder applies its vehicle to brands, packages and search (snippets/daro-vehicle-redirect)
  function onFinderSearch(direct, el) {
    const vehicle = window.DaroVehicle;
    const m = direct.match(/\/collections\/([^/?#]+)/);
    if (!vehicle || !m) return;
    const container = el && el.closest('.easysearch-container');
    vehicle.set(decodeURIComponent(m[1]), container ? vehicleName(container) : '');
  }

  // The Search button is a link (EasySearch's onclick saves the vehicle first); rewrite it as it's clicked
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest && event.target.closest('a[href*="/collections/"]');
      if (!link || !link.closest('[class*="easysearch-"]')) return;
      const direct = directVehicleUrl(link.getAttribute('href'));
      if (!direct) return;
      link.setAttribute('href', direct);
      onFinderSearch(direct, link);
    },
    true
  );

  // Auto-submit and garage searches navigate via easysearch.searchFormSubmit
  function patchSearchSubmit() {
    const es = window.easysearch;
    if (!es || typeof es.searchFormSubmit !== 'function' || es.searchFormSubmit.daroDirect) return;
    const original = es.searchFormSubmit;
    es.searchFormSubmit = function (holder) {
      const url = holder && holder.esparams && holder.esparams.url;
      const direct = url && directVehicleUrl(url);
      if (!direct) return original.apply(this, arguments);
      onFinderSearch(direct, holder);
      if (this.saveSearchParams(holder)) window.location.href = direct;
    };
    es.searchFormSubmit.daroDirect = true;
  }

  // Search forms, as they're sent: with a chosen vehicle, only parts that fit it (its v: tag, plus the v-many
  // products sections/main-search.liquid checks); otherwise, on a segment page, that segment plus universal
  // parts. Uses the hidden Tag filter (Search & Discovery; snippets/facets.liquid never shows it).
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      let path = '';
      try {
        path = new URL(form.getAttribute('action') || '', location.href).pathname;
      } catch (e) {
        return;
      }
      if (!/\/search\/?$/.test(path)) return;
      form.querySelectorAll('input[data-daro-scope]').forEach((input) => input.remove());
      const key = window.DaroVehicle && window.DaroVehicle.key;
      let values = [];
      if (key) values = [`v:${key}`, 'v-many'];
      else if (current && !form.querySelector('input[name="filter.p.tag"]')) values = [`seg-${current}`, 'seg-universal'];
      const add = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.dataset.daroScope = '';
        form.appendChild(input);
      };
      values.forEach((value) => add('filter.p.tag', value));
      if (key && !form.querySelector('[name="type"]')) add('type', 'product');
    },
    true
  );

  function init() {
    scan();
    // EasySearch injects its widget after load; watch for it without observing the whole page forever.
    const bodyObserver = new MutationObserver(() => {
      scan();
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => bodyObserver.disconnect(), 15000);
  }

  // Product page: Daro blocks marked [data-daro-swap][id] refresh from the new variant's HTML
  document.addEventListener('variant:update', (event) => {
    const source = event?.detail?.data?.html;
    if (!source || typeof source.querySelector !== 'function') return;
    document.querySelectorAll('[data-daro-swap][id]').forEach((el) => {
      const next = source.querySelector(`#${CSS.escape(el.id)}`);
      if (next) el.innerHTML = next.innerHTML;
    });
  });

  // Debug hook: DaroSegments.segmentFor('Audi', 'R8') in the console
  window.DaroSegments = { current, segmentFor, makeAllowed, modelAllowed };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
